// Wszystkie progi i parametry w jednym miejscu — łatwo je stroić po pierwszych sesjach z dzieckiem.

export const config = {
  scoring: {
    /** Minimalna kompletność (% słów wypowiedzianych) */
    minCompleteness: 80,
    /** Minimalna średnia dokładność wymowy */
    minAccuracy: 70,
    /** Słowo z dokładnością poniżej progu uznajemy za słabe (zgodnie z Azure: błąd wymowy < 60) */
    weakWordBelow: 60,
    /**
     * Gdy zwykłe rozpoznawanie usłyszało dokładnie to zdanie i jest pewne (patrz niżej), ocena głosek pojedynczego słowa jest tylko wskazówką,
     * a nie blokadą: słowo z oceną od tego progu w górę nie przekreśla zaliczenia (zdanie dostaje wtedy najwyżej 2★, a słowo jest wskazane
     * jako „do potrenowania”). Powód: Azure zaniża słowo tuż przed pauzą w zdaniu (wypowiedzi z pauzą 0,4–0,8 s dostawały 49–59, choć transkrypt słyszał
     * całe zdanie), a dzieci robią pauzy. Kalibracja na głosach syntezatora (2 przebiegi): 0 błędnych zaliczeń, błędnych odrzuceń 8 → 4; przy 40 pojawiają
     * się już błędne zaliczenia (słowo ucięte w połowie), więc margines jest niewielki. Ustaw tyle samo co weakWordBelow, żeby wyłączyć.
     */
    trustedWeakWordBelow: 50,
    /** Ile słabych słów jeszcze akceptujemy w zdaniu */
    allowedWeakWords: 0,
    /** Ile słów wzorca może zostać pominiętych. 0 = trzeba powiedzieć całe zdanie (komunikat „powiedz całe zdanie”, a nie „popracuj nad słowem”; pominięte słowo ma też ocenę 0, więc przy luzowaniu zwiększ i allowedWeakWords). */
    allowedOmissions: 0,
    /**
     * Niepewność zwykłego rozpoznawania (bez wzorca). Gdy oba rozpoznania „dopowiadają” brakującą końcówkę słowa („I want foo” → „food”),
     * ocena głosek tego nie widzi, ale samo rozpoznawanie jest wtedy wyraźnie mniej pewne: niska pewność i kilka prawie remisowych wersji tekstu.
     * Pierwotnie 0,70 (kalibracja na głosach syntezatora: poprawne nagrania miały pewność ≥ 0,79 z nielicznymi wyjątkami). Pierwszy prawdziwy głos
     * (dorosły Polak, wszystko powiedziane poprawnie, transkrypt = zdanie, 0 alternatyw) dostał jednak 0,65 i został odrzucony, a dziecko mówiące po
     * angielsku będzie rozpoznawane jeszcze mniej pewnie. Dlatego 0,55. Cena (bench, 265 przypadków „błędnych”): jedna podmiana jednej samogłoski
     * (want → went, pewność 0,64, ocena głosek 91–95) przechodzi, bo pewność 0,64 nie odróżnia jej od poprawnego głosu 0,65 — tego żadna wartość progu nie
     * rozdzieli. Ucięte słowa łapie i tak liczba alternatyw (poniżej). Przywrócenie 0,70 = ostrzej, ale i więcej odrzuceń poprawnej mowy.
     */
    minTranscriptConfidence: 0.55,
    /**
     * Gdy zwykłe rozpoznawanie ma choć jedną prawie remisową alternatywę, wymagamy wyższej pewności. Powód: „I want fu” (słowo „food” ucięte) dostało
     * pewność 0,65 i 1 alternatywę, a poprawne „The food is good” — 0,65 i 0 alternatyw. Sama pewność ich nie rozdziela, ale rozpoznawanie „rozdarte”
     * między dwie wersje tekstu to najmocniejszy sygnał niejasności. Na benchmarku ta reguła nic nie kosztuje (0 dodatkowych błędnych odrzuceń).
     * Ustaw tyle samo co minTranscriptConfidence, żeby wyłączyć.
     */
    minConfidenceWithAlternative: 0.7,
    /** Ile prawie remisowych alternatyw tekstu wolno (więcej = „nie jestem pewien, co usłyszałem”) */
    maxTiedAlternatives: 1,
    /**
     * Słaba końcówka zdania. Zwykłe rozpoznawanie dopowiada ostatnie słowo z kontekstu („I want fu” → „food”, „Is the food goo” → „good”) i robi to z dużą
     * pewnością oraz bez rywali, więc reguły pewności tego nie łapią. Z 22 opisanych prób na prawdziwym głosie (2026-10-14): 4 z 14 celowo błędnych
     * zostało zaliczonych — we wszystkich końcowa głoska zdania to zwarta (d) oceniona 62–81 przy pewności rozpoznania 0,68–0,87, a w poprawnie
     * wypowiedzianych zdaniach kończących się na d/t ta głoska miała zawsze 100 (pewność ≥ 0,91). Reguła: ostatnia głoska zdania jest zwartą (d t k p b g)
     * z oceną poniżej `finalStopMinAccuracy` ORAZ pewność rozpoznania poniżej `finalStopMaxConfidence` → „niejasne” (powtórz wyraźnie). Obie
     * części są potrzebne: sama ocena końcowej zwartej odrzucałaby 20% poprawnych nagrań syntezatora (np. głos „ana” w „I want to eat”, t = 71–81,
     * ale rozpoznawanie pewne ≥ 0,9), a z warunkiem pewności 9%. UWAGA: progi opierają się na 7 próbach z końcową zwartą od jednego mówcy —
     * to hipoteza do potwierdzenia (kolejna runda w trybie testowym: poprawne zdania kończące się na d/t/k). Wyłączenie: `finalStopMinAccuracy: 0`.
     */
    finalStopMinAccuracy: 90,
    finalStopMaxConfidence: 0.9,
    /** Próg dla 3 gwiazdek (zdanie musi być też zaliczone i bez słabych słów) */
    threeStarsAccuracy: 85,
    /** Od tylu % (dokładności całego zdania, brakujące słowo liczy się jako 0) próba ma 2 gwiazdki nawet bez zaliczenia — „prawie” też jest postępem */
    twoStarsAccuracy: 70,
    /** Ile razy dziecko może próbować to samo zdanie, zanim przejdziemy dalej */
    maxAttempts: 3,
  },
  /**
   * Procent wymowy zdania = dokładność z oceny (średnia ocen słów zdania; słowo pominięte liczy się jako 0, więc osobna kara za kompletność
   * byłaby podwójna). Gwiazdki wynikają z procentu (progi wyżej: 3★ od `threeStarsAccuracy`, 2★ od `twoStarsAccuracy`), a zaliczenie zdania
   * to osobna decyzja (patrz `decide`). Procent pokazujemy przy zdaniu (tylko przy ocenie Azure) i jako średnią w podsumowaniu bloku.
   */
  percent: {
    /** Czy pokazywać procent po każdej próbie (obok gwiazdek). */
    showPerSentence: true,
    /** Od tylu % średniej komunikat „świetnie” */
    great: 90,
    /** Od tylu % „bardzo dobrze” (poniżej, ale od minAccuracy: „dobrze”; jeszcze niżej: „ćwiczymy dalej”) */
    good: 80,
  },
  /**
   * Poza maskotki po próbie „powiedz zdanie” (lib/reaction.ts): od jakiego procentu (ostro większego) puchar i radość oraz poniżej jakiego procentu
   * wzruszenie ramion i „hmm”. Zaliczone zdania poniżej `cheer` dostają spokojną pozę (kawa albo leżak na zmianę), a ostatnia próba bez zaliczenia — sen.
   */
  reaction: {
    trophy: 90,
    cheer: 80,
    shrug: 60,
    think: 50,
  },
  srs: {
    /** Odstępy w dniach dla pudełek Leitnera 1–5 */
    intervals: { 1: 1, 2: 3, 3: 7, 4: 14, 5: 30 } as Record<1 | 2 | 3 | 4 | 5, number>,
    /** Maks. liczba zaległych słówek dokładanych do lekcji */
    reviewLimit: 5,
    /** Maks. liczba słówek w sesji "tylko powtórka" */
    reviewOnlyLimit: 10,
  },
  session: {
    /** Ile zdań (z wariantów) układamy z kawałków i mówimy na końcu */
    buildCount: 3,
    /**
     * Po ilu nieudanych próbach w „Uzupełnij” i „Ułóż zdanie” pojawia się przycisk „Popraw” (pokazuje poprawną odpowiedź i idzie dalej).
     * Próby nie są ograniczone: dziecko może dalej sprawdzać, a nic nie rozwiązuje się samo.
     */
    fixAfterAttempts: 3,
  },
  /**
   * Rozmowa z botem (Claude API; §14.4 w SPEC.md). Model pisze tekst, a o tym, co wolno powiedzieć, decyduje kod: odpowiedź przechodzi kontrolę
   * słownictwa i długości (jedna poprawka, potem gotowa linijka z puli bota). Wszystko tu można stroić po pierwszych rozmowach.
   */
  chat: {
    /** Ile razy dziecko odpowiada w jednej rozmowie (bot dodatkowo zaczyna i żegna się). */
    turns: 5,
    /** Najwięcej słów w wypowiedzi bota (angielskiej). */
    maxWords: 15,
    /** Najwięcej słów w polskim wyjaśnieniu. */
    maxPlWords: 40,
    /** Imię bota: model może je powiedzieć („I am Robo”), więc kontrola słownictwa je przepuszcza. */
    botName: 'Robo',
    /** Najwięcej znaków jednej wypowiedzi dziecka wysyłanej do modelu (reszta jest ucinana). */
    maxChildChars: 300,
    /** Domyślny model (nadpisuje go zmienna CHAT_MODEL na serwerze). Mały i szybki wystarcza: odpowiedzi mają kilkanaście słów. */
    model: 'claude-haiku-5-5',
    /** Limit długości odpowiedzi modelu w tokenach. */
    maxTokens: 300,
    /** Ile ms czekamy na odpowiedź Anthropic, zanim uznamy próbę za nieudaną. */
    timeoutMs: 20_000,
    /** Dzienny limit odpowiedzi bota na jedno urządzenie (nadpisuje go DAILY_CHAT_LIMIT). Osobna rozmowa to ok. 6 odpowiedzi, a lekcja z botem w zadaniach kilkadziesiąt (reakcje po próbach, pytania). */
    dailyLimit: 400,
    /**
     * Słowa, których bot może użyć poza słownictwem dziecka (podstawa + słówka z dni do dziś). Dziecko słyszy je codziennie w lekcjach
     * („Great job!”, „Listen…”, „Say it again”), więc ich nie liczymy jako nowych. Do dozwolonych wchodzą też wszystkie słowa z linijek bota
     * (banter.json: „Welcome back!”, „Your brain is warm…”) i imię bota (`botName`). Imiona dzieci, liczby i inne słowa są zabronione.
     */
    extraWords: ['oh', 'hmm', 'yay', 'great', 'nice', 'super', 'job', 'perfect', 'excellent', 'tomorrow', 'goodbye', 'wow', 'okay', 'ok', 'well', 'again', 'say', 'try', 'listen', 'sorry', 'bye', 'welcome', 'robot', 'tell', 'about', 'so', 'talk', 'chat',
      // polecenia szkolne i pochwały (patch-29, na podstawie benchu z 2026-10-14: bot ~58% odpowiedzi tracił na tych słowach). Dziecko słyszy je codziennie w lekcjach.
      'look', 'let', 'us', 'next', 'put', 'take', 'think', 'give', 'tap', 'order', 'right', 'end', 'done', 'together', 'clear', 'clearly', 'slow', 'slowly', 'hard', 'aloud', 'start', 'ready', 'wait', 'help',
      'word', 'words', 'sentence', 'read', 'repeat', 'sound', 'card', 'cards', 'picture', 'screen', 'answer', 'oops',
      'learn', 'learned', 'learning', 'doing', 'much', 'whole', 'proud', 'more', 'little', 'loud', 'louder'] as readonly string[],
    /** Głosy Azure Neural dla bota w rozmowie (nazwy z katalogu głosów Azure; region musi je obsługiwać). */
    voices: { en: 'en-US-JennyNeural', pl: 'pl-PL-ZofiaNeural' },
    /** Tempo głosu bota względem normalnego (SSML prosody rate) — dla dziecka trochę wolniej. */
    speechRate: '-10%',
  },
  /**
   * Bot jako warstwa w zadaniach (SPEC §14.5): przycisk „Zapytaj bota”, krótkie reakcje po próbach i komentarz na koniec lekcji.
   * Ocenę nadal robi kod — model dostaje wynik jako fakt. Warstwa nie może spowalniać lekcji: reakcja ma krótki limit czasu i jedną próbę,
   * a po kilku porażkach zapytań wyłącza się do końca sesji (lekcja idzie dalej na gotowych nagraniach).
   */
  coach: {
    /** Domyślnie włączona; rodzic może ją wyłączyć w „Dla rodzica”. */
    enabledByDefault: true,
    /** Po zaliczonej próbie: 'never' | 'notable' (gdy nie 3★ za pierwszym razem albo jest słabe słowo) | 'always'. Po niezaliczonej bot reaguje zawsze. */
    reactOnPass: 'notable' as 'never' | 'notable' | 'always',
    /** Najwięcej słów w reakcji po próbie (krótko, bo dziecko czeka na kolejną próbę). */
    maxReactWords: 10,
    /** Zapas ponad `maxReactWords`, który kontrola jeszcze przepuszcza (model dostaje w poleceniu limit ścisły, ale często przekracza go o 1–2 słowa — to nie jest powód, żeby odrzucić dobrą reakcję). */
    reactWordsSlack: 2,
    /** Ile ms czekamy na reakcję po próbie (potem gra gotowa linijka z nagraniem). */
    reactTimeoutMs: 6_000,
    /** Ile ms czekamy na odpowiedź na pytanie dziecka. */
    askTimeoutMs: 15_000,
    /** Ile pytań dziecko może zadać w jednym otwarciu panelu „Zapytaj”. */
    askTurns: 3,
    /** Po tylu kolejnych nieudanych zapytaniach warstwa wyłącza się do końca sesji. */
    giveUpAfter: 2,
    /** Język nasłuchu pytań: dziecko zwykle pyta po polsku („co to znaczy?”). */
    askLang: 'pl-PL' as 'pl-PL' | 'en-US',
  },
  /**
   * Nauczyciel w lekcji (patch-30): po próbie bot mówi, co usłyszał i co poprawić, składając uwagę z gotowych nagrań (src/content/teacher.json
   * i nagrania słów) — bez modelu, bez opóźnienia, tym samym głosem co reszta lekcji.
   */
  teacher: {
    /** Język uwag bota, gdy rodzic go nie zmienił: 'en' (angielski) albo 'pl' (polski). */
    defaultLang: 'en' as 'en' | 'pl',
    /**
     * „Usłyszałem: add” bot mówi tylko wtedy, gdy ma co do tego pewność: zwykłe rozpoznawanie (bez wzorca) jest pewne na co najmniej tyle
     * (i nie ma prawie remisowych wersji), albo oba rozpoznania — ze wzorcem i bez — usłyszały to samo słowo. W przeciwnym razie mówi samo „Powiedz: eat”.
     */
    heardMinConfidence: 0.6,
    /** Przerwa między klockami uwagi (ms), żeby frazy i słowa nie zlewały się w jedno. */
    gapMs: 140,
  },
  /**
   * AI (Claude) w aplikacji — WSTRZYMANE do odwołania (decyzja 2026-10-14): pasek „Zapytaj bota”, reakcje modelu i rozmowa z botem są wyłączone.
   * Kod i serwer (/api/chat) zostają. Włączenie: `enabledByDefault: true` (albo w przeglądarce klucz `naative.ai.v1` = "on" — do testów).
   */
  ai: {
    enabledByDefault: false,
  },
  /** Wygląd (patch-31). */
  ui: {
    /** „Poziom” pod imieniem na ekranie głównym — płaski (bez liczenia), na życzenie: poziom stały + gwiazdki konta. */
    level: 1,
    /** „Najczęściej: …” na „Moim tygodniu” pojawia się dopiero po tylu dniach zbierania buziek (od pierwszego zapisanego nastroju); wcześniej jest informacja o zbieraniu. */
    moodSummaryMinDays: 7,
  },
} as const;

export type Config = typeof config;
